import {Component, OnDestroy, OnInit} from '@angular/core';
import {Router} from '@angular/router';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {select, Store} from '@ngrx/store';
import {ForgetPassword, getError, getForgetPassword, getLoggedInUser, IAuthenticationState, ResetAuthState} from '@app/stores';
import {ToastrService} from 'ngx-toastr';
import {TranslateService} from '@ngx-translate/core';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {mergeAuthTranslations} from '../auth-i18n';

@Component({
  templateUrl: './forgot-password.component.html',
  styleUrls: ['./forgot-password.component.css']
})
export class ForgotPasswordComponent implements OnInit, OnDestroy {
  passwordForm: UntypedFormGroup;
  submitted = false;
  passMessage: any = {
    success: false,
    msg: ''
  };
  // True from the moment the address is sent until the store answers - with the
  // request accepted or with an error. The button is disabled meanwhile, so one
  // click is one request.
  sendPending = false;
  unsubscriber = new Subject();

  constructor(
    private formBuilder: UntypedFormBuilder,
    private router: Router,
    private toastrService: ToastrService,
    private authenticationStore: Store<IAuthenticationState>,
    private translate: TranslateService
  ) {
    this.authenticationStore.dispatch(ResetAuthState({params: {error: '', forgetPassword: null}}));
    this.subscribeStores();
  }

  subscribeStores() {
    // redirect to home if already logged in
    this.authenticationStore.pipe(select(getLoggedInUser))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(user => {
        if (user) {
          this.router.navigate(['/']);
        }
      });

    this.authenticationStore.pipe(select(getForgetPassword))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(forgetPassword => {
        if (forgetPassword) {
          this.sendPending = false;
          this.passMessage = forgetPassword;
          if (forgetPassword.success) {
            this.toastrService.success('Password reset link has been sent to your email.');
            this.router.navigate(['/login']);
          }
        }
      });

    this.authenticationStore.pipe(select(getError))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(error => {
        if (error) {
          this.sendPending = false;
          this.toastrService.error(error);
        }
      });
  }

  ngOnInit() {
    mergeAuthTranslations(this.translate, this.unsubscriber);

    this.passwordForm = this.formBuilder.group({
      email: ['', [Validators.required, Validators.email]]
    });
  }

  get f() {
    return this.passwordForm.controls;
  }

  /**
   * Whatever the server said about the request, if it said anything.
   *
   * The reply is deliberately the same for every address - it never says whether
   * an account exists - so it is safe to show verbatim, and safer than wording of
   * our own that might not be.
   */
  get responseMessage(): string {
    return this.passMessage?.message || this.passMessage?.msg || '';
  }

  /**
   * Returns success message object that will use for Showing the response.
   *
   * @param  url  an absolute URL giving the reset Forgot password page of the Application.
   * @param  name the location of the request, relative to the url.
   * @return      success object with temporary login URL.
   */

  onSubmit() {
    this.submitted = true;

    // stop here if form is invalid, or the previous request has not answered yet
    if (this.passwordForm.invalid || this.sendPending) {
      return;
    }
    this.sendPending = true;
    this.authenticationStore.dispatch(ForgetPassword({email: this.passwordForm.value.email}));
  }

  ngOnDestroy() {
    this.unsubscriber.next(undefined);
    this.unsubscriber.complete();
  }
}
